import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

// Animation for bubble rising
const rise = keyframes`
  0% {
    bottom: -100px;
    opacity: 0;
  }
  10% {
    opacity: 0.8;
  }
  100% {
    bottom: 100vh;
    opacity: 0;
  }
`;

// Animation for bubble inflation
const inflate = keyframes`
  0% {
    transform: scale(0.5);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(0.8);
  }
`;

// Animation for bubble popping
const pop = keyframes`
  0% {
    transform: scale(1);
  }
  80% {
    transform: scale(1.5);
    opacity: 0.8;
  }
  100% {
    transform: scale(0.1);
    opacity: 0;
  }
`;

const BubbleContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
`;

const Bubble = styled.div`
  position: absolute;
  bottom: -50px;
  background: radial-gradient(
    circle at 30% 30%,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.15) 40%,
    rgba(255, 255, 255, 0.2) 60%,
    rgba(255, 255, 255, 0.1) 70%,
    rgba(255, 255, 255, 0.05) 100%
  );
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.3), inset 0 0 10px rgba(255, 255, 255, 0.1);
  animation: ${rise} ${props => props.duration}s linear infinite,
             ${props => (props.popping ? pop : inflate)} ${props => props.inflateDuration}s ease-in-out infinite;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  left: ${props => props.position}%;
  opacity: ${props => props.opacity};
  animation-delay: ${props => props.delay}s;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: ${props => props.size * 0.15}px;
    left: ${props => props.size * 0.15}px;
    width: ${props => props.size * 0.25}px;
    height: ${props => props.size * 0.25}px;
    background: radial-gradient(
      circle at center,
      rgba(255, 255, 255, 0.8) 0%,
      rgba(255, 255, 255, 0.2) 100%
    );
    border-radius: 50%;
    filter: blur(1px);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: ${props => props.size * 0.2}px;
    right: ${props => props.size * 0.2}px;
    width: ${props => props.size * 0.15}px;
    height: ${props => props.size * 0.15}px;
    background: radial-gradient(
      circle at center,
      rgba(255, 255, 255, 0.6) 0%,
      rgba(255, 255, 255, 0.1) 100%
    );
    border-radius: 50%;
    filter: blur(1px);
  }
`;

const Bubbles = () => {
  const [bubbles, setBubbles] = useState([]);
  
  // Generate random properties for a bubble
  const generateBubble = () => {
    const id = Math.random().toString(36).substring(7);
    // Vary bubble sizes more naturally - most bubbles small, some medium, few large
    const sizeRandom = Math.random();
    let size;
    if (sizeRandom < 0.6) { // 60% small bubbles
      size = Math.random() * 25 + 15; // 15-40px
    } else if (sizeRandom < 0.9) { // 30% medium bubbles
      size = Math.random() * 35 + 40; // 40-75px
    } else { // 10% large bubbles
      size = Math.random() * 45 + 75; // 75-120px
    }
    
    const position = Math.random() * 100; // Position between 0% and 100%
    
    // Smaller bubbles rise faster than larger ones (more realistic physics)
    const speedFactor = 1.5 - (size / 120); // Smaller bubbles have higher speedFactor
    const duration = (Math.random() * 6 + 6) / speedFactor; // Adjusted for size
    
    const delay = Math.random() * 2; // Delay between 0s and 2s
    
    // Larger bubbles are more transparent
    const opacity = Math.max(0.2, Math.min(0.9, 0.9 - (size / 200)));
    
    // Larger bubbles inflate/deflate slower
    const inflateDuration = Math.random() * 2 + 1 + (size / 60);
    
    // Larger bubbles more likely to pop
    const popThreshold = 0.6 - (size / 300);
    const popping = Math.random() > popThreshold;
    
    return { id, size, position, duration, delay, opacity, inflateDuration, popping };
  };
  
  // Initialize bubbles
  useEffect(() => {
    // Create initial bubbles
    const initialBubbles = [];
    for (let i = 0; i < 25; i++) {
      initialBubbles.push(generateBubble());
    }
    setBubbles(initialBubbles);
    
    // Add a new bubble every second
    const intervalId = setInterval(() => {
      setBubbles(prevBubbles => {
        // Remove some old bubbles if there are too many
        if (prevBubbles.length > 40) {
          const newBubbles = [...prevBubbles];
          newBubbles.shift();
          return [...newBubbles, generateBubble()];
        }
        return [...prevBubbles, generateBubble()];
      });
    }, 1000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  return (
    <BubbleContainer>
      {bubbles.map(bubble => (
        <Bubble
          key={bubble.id}
          size={bubble.size}
          position={bubble.position}
          duration={bubble.duration}
          delay={bubble.delay}
          opacity={bubble.opacity}
          inflateDuration={bubble.inflateDuration}
          popping={bubble.popping}
        />
      ))}
    </BubbleContainer>
  );
};

export default Bubbles;
